import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SubsidyInputs } from '@/types/businesscase';
import { useState, useEffect } from 'react';
import { InfoIcon } from 'lucide-react';
import { formatCurrency } from '@/lib/businesscaseCalculations';

interface SubsidyInputCardProps {
  subsidies: SubsidyInputs;
  totalInvestment: number;
  eiaValue: number;
  onUpdate: (subsidies: SubsidyInputs) => Promise<void>;
}

export function SubsidyInputCard({ subsidies, totalInvestment, eiaValue, onUpdate }: SubsidyInputCardProps) {
  const [localSubsidies, setLocalSubsidies] = useState(subsidies);
  const [saving, setSaving] = useState(false);

  // Debounced save (except for toggles)
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (JSON.stringify(localSubsidies) !== JSON.stringify(subsidies)) {
        setSaving(true);
        try {
          await onUpdate(localSubsidies);
        } finally {
          setSaving(false);
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [localSubsidies]);

  const handleToggle = async (field: 'eiaEnabled' | 'flexEEnabled', value: boolean) => {
    let updated = { ...localSubsidies, [field]: value };
    
    if (field === 'flexEEnabled') {
      if (value) {
        // Auto-calculate when enabled: 35% of total investment
        updated.flexEValue = Math.round(totalInvestment * 0.35);
      } else {
        // Reset to 0 when disabled
        updated.flexEValue = 0;
      }
    }
    
    setLocalSubsidies(updated);
    
    // Save immediately for toggles
    setSaving(true);
    try {
      await onUpdate(updated);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof SubsidyInputs, value: string | number) => {
    const numValue = typeof value === 'string' ? (parseFloat(value) || 0) : value;
    setLocalSubsidies(prev => ({ ...prev, [field]: numValue }));
  };

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Subsidies & Belastingen
          {saving && <span className="text-xs text-muted-foreground">Opslaan...</span>}
        </CardTitle>
        <CardDescription>Configureer subsidies en belastingtarieven</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* EIA Toggle */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label htmlFor="eia-toggle">EIA (40% aftrek)</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <InfoIcon className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-semibold mb-1">Energie Investerings Aftrek</p>
                    <p className="text-sm">40% van de investering mag worden afgetrokken van de fiscale winst.</p>
                    <p className="text-sm mt-1">Bij een belastingtarief van 25% levert dit een netto voordeel op van ongeveer 10%.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Switch 
              id="eia-toggle"
              checked={localSubsidies.eiaEnabled}
              onCheckedChange={(checked) => handleToggle('eiaEnabled', checked)}
            />
          </div>

          {localSubsidies.eiaEnabled && (
            <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-800 dark:text-green-200">
                <strong>EIA Waarde:</strong> {formatCurrency(eiaValue)}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                = (€{totalInvestment.toLocaleString('nl-NL')} × 40%) × {localSubsidies.taxRate}%
              </p>
            </div>
          )}
        </div>

        {/* Tax Rate */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="tax-rate">Belastingtarief (%)</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <InfoIcon className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm">Het vennootschapsbelastingtarief</p>
                  <p className="text-sm">Standaard 25% in Nederland</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Input
            id="tax-rate"
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={localSubsidies.taxRate}
            onChange={(e) => handleChange('taxRate', e.target.value)}
            className="font-mono"
          />
        </div>

        {/* Flex-E Toggle */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label htmlFor="flexe-toggle">Flex-E Subsidie</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <InfoIcon className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-sm">Subsidieregeling voor flexibiliteit energiesysteem</p>
                    <p className="text-sm mt-1">Vul handmatig het bedrag in</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Switch 
              id="flexe-toggle"
              checked={localSubsidies.flexEEnabled}
              onCheckedChange={(checked) => handleToggle('flexEEnabled', checked)}
            />
          </div>
          
          {localSubsidies.flexEEnabled && (
            <div className="space-y-2 pl-6">
              <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Flex-E Subsidie:</strong> {formatCurrency(localSubsidies.flexEValue || 0)}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  = €{totalInvestment.toLocaleString('nl-NL')} × 35%
                </p>
              </div>
              
              <Label htmlFor="flex-e-value">Flex-E Bedrag Aanpassen (€)</Label>
              <Input
                id="flex-e-value"
                type="number"
                value={localSubsidies.flexEValue || 0}
                onChange={(e) => handleChange('flexEValue', e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Standaard 35% van totale investering, handmatig aanpasbaar
              </p>
            </div>
          )}
        </div>

        {/* Other Subsidy */}
        <div className="space-y-2">
          <Label htmlFor="other-subsidy">Overige Subsidies (€)</Label>
          <Input
            id="other-subsidy"
            type="number"
            value={localSubsidies.otherSubsidy}
            onChange={(e) => handleChange('otherSubsidy', e.target.value)}
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            {formatCurrency(localSubsidies.otherSubsidy)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
